// client/src/utils/catalogueSource.ts
//
// What a Master Catalogue row's own `source` means as the pricing label a
// quotation line carries.
//
// A catalogue row records where it came from (`MasterCatalogue.source`:
// 'vendor_invoice' | 'quotation_historical' | 'price_list' | 'manual' |
// 'unknown'). A quotation line records what priced it
// (`QuotationItemSchema.pricingSource`). Three routes lead a line to a
// catalogue row — the pricing pipeline's catalogue step, the supplier dropdown,
// and the Master Catalogue search popup on the quotation page — and all three
// must report the same origin for the same row, or the screen contradicts
// itself depending on which one a reviewer used.
//
// This was implemented twice: `mapCatalogueSourceToPricingSource` in
// `quotationGeneration.service.js` and a ternary chain inside
// `QuotationDetailPage.handleCatalogueSelect`. They agreed until 2026-09-04,
// when only the server copy learned that a hand-entered row is not a price list
// (QUO-00017) — exactly the drift this repo has been burned by. One owner now,
// mirrored.
//
// MIRRORED FILE: src/utils/catalogueSource.js is a line-for-line twin.
// CRA's ModuleScopePlugin forbids importing from src/, so the copy is the only
// option. Change one, change both — `mirroredUtilParity.test.js` fails if you
// do not.

/** A `MasterCatalogue.source` value, as a `QuotationItemSchema.pricingSource` value. */
export function mapCatalogueSourceToPricingSource(src: string): string {
  if (src === 'vendor_invoice') return 'vendor_invoice';
  if (src === 'quotation_historical') return 'historical_quotation';
  // A row somebody TYPED. It used to fall through to 'price_list' below, which
  // was the closest available lie: a hand-entered row is on nobody's price
  // list, and the line read "Source: Price List" over a number a person had
  // entered themselves.
  if (src === 'manual') return 'master_catalogue';
  // 'price_list' and 'unknown' both land here. 'unknown' is the schema default
  // for rows that predate the field; guessing those were hand-entered would be
  // the same mistake in the other direction.
  return 'price_list';
}
